import React, { useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import CurrencyInputField from "../Common/CurrencyInput/CurrencyInputField";
import WebService from "../../Services/WebService";
import {
  getProfileJourneyInfo,
  updateProfileJourney,
} from "../../Services/CommonFunction";
import TagsInput from "react-tagsinput";
import { Info } from "react-bootstrap-icons";
import Calender from "../Common/Calender/Calender";
import HelperService from "../../Services/HelperService";
import { Label } from "../Common/Label/Label";

// ─── Types ────────────────────────────────────────────────────────────────────
interface Field {
  key: string;
  label: string;
  field_type: "text" | "textarea" | "dropdown" | "email" | "number";
  placeholder?: string;
  options?: string[];
  required?: boolean;
}

interface Section {
  title: string;
  description: string;
  icon: string;
  fields: Field[];
}

interface ProfileJourneyModalProps {
  show: boolean;
  onHide: () => void;
  sectionKey: string;
  onSave?: (data: Record<string, string>) => void;
  onSubmitSuccess?: () => void;
  onSkip?: () => void;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ProfileJourneySection({
  show,
  onHide,
  sectionKey,
  onSave,
  onSubmitSuccess,
  onSkip,
}: Partial<ProfileJourneyModalProps>) {
  const [formData, setFormData] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  let dispatch = useDispatch();
  let profile_journey_info = useSelector(
    (state: any) => state?.items?.profile_journey_info || null,
  );
  console.log(profile_journey_info, "==>profile");

  const sections = profile_journey_info?.sections || [];
  const section = sections.find((item: any) => item.key === sectionKey);
  let type = profile_journey_info?.type?.toLowerCase() || "";
  let uuid = profile_journey_info?.uuid || "";

  const handleSave = async () => {
    if (Object.keys(formData || {}).length === 0) {
      onSubmitSuccess?.();
      onHide?.();
    } else {
      if (!type || !uuid) return;
      try {
        // WebService.addLoader("save_profile_journey_btn");
        setSaving(true);
        const payload = { ...formData };
        const response = await updateProfileJourney(type, uuid, payload);
        if (response?.status) {
          getProfileJourneyInfo(type, uuid, dispatch);
          toast.success(response?.message || "Data saved successfully");
          onSave?.(payload);
          onSubmitSuccess?.();
          onHide?.();
        }
      } catch (error) {
        console.log(error);
      } finally {
        setSaving(false);
        // WebService.removeLoader("save_profile_journey_btn");
      }
    }
  };
  const handleFieldChange = (key: string, value: any) => {
    console.log(key, value);
    setFormData((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };
  const fieldsPerPage = 4;

  const topFields = useMemo(
    () =>
      [...(section?.fields || [])]
        .sort((a: any, b: any) => (a.priority || 999) - (b.priority || 999))
        .slice(0, fieldsPerPage),
    [section?.fields],
  );

  const totalFields = topFields?.length || 0;
  const completedFields = useMemo(() => {
    return (
      topFields?.filter((field: any) => {
        const val = formData[field.key];
        if (Array.isArray(val)) return val.length > 0;
        if (typeof val === "string") return val.trim().length > 0;
        return val !== undefined && val !== null;
      }).length || 0
    );
  }, [formData, topFields]);

  const progressPercent =
    totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;

  if (!show || !section?.fields?.length) {
    return null;
  }
  const renderField = (field: any) => {
    switch (field.field_type) {
      case "textarea":
        return (
          <Form.Control
            as="textarea"
            placeholder={field.placeholder}
            value={formData[field.key] || ""}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            rows={4}
            maxLength={field.max_length}
          />
        );

      case "dropdown":
        const selectOptions =
          field.options?.map((option: any) => ({
            value: option.title,
            label: option.title,
            data: option,
          })) || [];

        const isMulti = field.selection_type === "multi";

        const selectedValue = isMulti
          ? selectOptions.filter((option: any) =>
              Array.isArray(field.value)
                ? field.value.some(
                    (item: any) =>
                      item?.id === option.value ||
                      item === option.label ||
                      item?.title === option.label,
                  )
                : false,
            )
          : selectOptions.find(
              (option: any) =>
                option.value === field.value || option.label === field.value,
            ) || null;

        return (
          <Select
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            closeMenuOnScroll={false}
            menuPlacement="auto"
            options={selectOptions}
            isMulti={isMulti}
            placeholder={field.placeholder}
            styles={{
              menuPortal: (base) => ({
                ...base,
                zIndex: 999999,
              }),
            }}
            value={
              isMulti
                ? selectOptions.filter((option: any) =>
                    (formData[field.key] || []).some(
                      (item: any) =>
                        item?.id === option.value ||
                        item === option.label ||
                        item?.title === option.label,
                    ),
                  )
                : selectOptions.find(
                    (option: any) =>
                      option.value === formData[field.key] ||
                      option.label === formData[field.key],
                  ) || null
            }
            onChange={(value: any) => {
              if (isMulti && field.limit && value?.length > field.limit) {
                toast.warn(`You can select maximum ${field.limit} options`);
                return;
              }

              handleFieldChange(
                field.key,
                isMulti
                  ? value.map((item: any) => ({
                      id: item.value,
                      title: item.label,
                    }))
                  : value?.value,
              );
            }}
          />
        );

    case "radio":
  return (
    <div className="d-flex flex-wrap gap-3 mt-2">
      {field.options?.map((option: any) => (
        <div key={option.value} className="form-check">
          <input
            type="radio"
            className="form-check-input"
            id={`${field.key}_${option.value}`}
            name={field.key}
            value={option.value}
            checked={
              String(formData[field.key] ?? field.value ?? "") ===
              String(option.value)
            }
            onChange={(e) =>
              handleFieldChange(field.key, e.target.value)
            }
          />

          <label
            className="form-check-label ms-1"
            htmlFor={`${field.key}_${option.value}`}
          >
            {option.title}
          </label>
        </div>
      ))}
    </div>
  );

      case "datepicker_year":
        return (
          <Calender
            value={formData[field.key] || ""}
            setValue={(value: Date | null) => {
              if (!value) {
                handleFieldChange(field.key, null);
                return;
              }
              const fromYear = value.getFullYear();
              const toYear = new Date(value);
              toYear.setFullYear(fromYear + 1);
              handleFieldChange(field.key, toYear?.getFullYear().toString());
            }}
          />
        );

      case "currency_input":
        return (
          <CurrencyInputField
            placeHolder={field.placeholder}
            id={field.key}
            prefix={field.prefix || ""}
            suffix={field.suffix || ""}
            value={formData[field.key] || ""}
            setValue={(value: string) => {
              let updatedValue = value;

              if (field?.suffix === "%") {
                // Keep only digits and allow max 3 digits
                updatedValue = value.replace(/\D/g, "").slice(0, 3);
              }

              handleFieldChange(field.key, updatedValue);
            }}
            maxLength={field?.suffix === "%" ? 3 : undefined}
          />
        );

      case "link_input":
        return (
          <>
            <input
              type="text"
              className="form-control"
              placeholder={field.placeholder}
              value={formData[field.key] || ""}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
            />

            {formData[field.key] &&
              !HelperService.checkWebUrl(formData[field.key]) && (
                <Label modeError={true} title="Please enter valid link" />
              )}
          </>
        );

      case "input_tags":
        return (
          <TagsInput
            inputProps={{
              placeholder: "Please hit ENTER to save keyword",
              height: "200px",
            }}
            value={
              formData?.top_keywords?.length > 0
                ? formData?.top_keywords?.map((ele: any) => ele?.title)
                : []
            }
            preventSubmit
            addOnPaste
            onlyUnique
            onChange={(tags: string[]) => {
              if (tags?.length > 5) {
                toast.warn("Max 5 keywords allowed!");
                return;
              }
              setFormData((prev: any) => ({
                ...prev,
                top_keywords: tags.map((tag) => ({ title: tag })),
              }));
            }}
          />
        );

      default:
        return (
          <input
            type="text"
            className="form-control"
            placeholder={field.placeholder}
            value={formData[field.key] || ""}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
          />
        );
    }
  };
  // const fullWidthTypes = ["textarea", "dropdown", "input_tags", "input_tags"];

  return (
    <>
      {/* ── Backdrop ── */}
      <div className="pj-backdrop" onClick={onHide} />

      {/* ── Dialog ── */}
      <div
        className="pj-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={section.title}
      >
        {/* Accent stripe */}
        <div className="pj-stripe" />

        {/* Header */}
        <div className="pj-header">
          <div className="pj-header-left">
            <div className="pj-badge">
              <span className="pj-badge-dot" />
              <span>AI Smart Recommendations</span>
            </div>
            <h3 className="pj-title">
              <span className="pj-icon" aria-hidden="true">
                {section.icon}
              </span>
              {section.title}
            </h3>
            <p className="pj-subtitle">
              Complete a few details to unlock high-probability AI opportunities
            </p>
          </div>
          <button className="pj-close" onClick={onHide} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1l12 12M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Smart Progress Indicator */}
        <div className="pj-progress-wrap">
          <div className="pj-progress-header">
            <span className="pj-progress-label">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6, verticalAlign: -1 }}>
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              Match Readiness
            </span>
            <span className="pj-progress-pct">{progressPercent}% completed</span>
          </div>
          <div className="pj-progress-track">
            <div
              className="pj-progress-fill"
              style={{ width: `${Math.max(progressPercent, 8)}%` }}
            />
          </div>
        </div>

        {/* Body */}
        <div className="pj-body">
          {/* Description callout */}
          <div className="pj-callout">
            <svg
              className="pj-callout-icon"
              viewBox="0 0 20 20"
              fill="none"
              width="18"
              height="18"
            >
              <path
                d="M10 2a8 8 0 100 16A8 8 0 0010 2zm0 4a1 1 0 110 2 1 1 0 010-2zm0 3.5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 9.5z"
                fill="currentColor"
              />
            </svg>
            <span>{section.description}</span>
          </div>

          {/* Fields grid */}
          <div className="pj-fields">
            {topFields?.map((field: any) => (
              <div
                key={field.key}
                // className={`pj-field ${fullWidthTypes.includes(field.field_type) ? "full" : ""}`}
                className={`pj-field full`}
              >
                <label className="pj-label">
                  {field.label}
                  {field?.limit && `(Max ${field?.limit})`}
                  {field.required && <span className="pj-req">*</span>}
                </label>
                {renderField(field)}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="pj-footer">
          <button
            className="pj-btn-skip"
            onClick={() => {
              onSkip?.();
              onHide?.();
            }}
          >
            Skip for now
          </button>
          <button
            id="save_profile_journey_btn"
            disabled={saved || saving}
            className={`pj-btn-save ${saving ? "loading" : ""} ${saved ? "success" : ""}`}
            onClick={handleSave}
          >
            {saved ? (
              <>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M3 8l4 4 6-7"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Saved!
              </>
            ) : saving ? (
              <>
                <span className="pj-spinner" />
                Saving…
              </>
            ) : (
              <>
                Save &amp; continue
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M3 8h10M9 4l4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── Styles ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

  .pj-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.65);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: 1040;
    animation: pj-fadein 0.25s ease;
  }

  .pj-dialog {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1050;
    width: min(580px, calc(100vw - 32px));
    max-height: min(90vh, 760px);
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border-radius: 20px;
    border: 1px solid rgba(102, 193, 94, 0.22);
    box-shadow: 0 25px 60px -12px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(102, 193, 94, 0.1);
    font-family: 'Plus Jakarta Sans', system-ui, -apple-system, sans-serif;
    animation: pj-slidein 0.3s cubic-bezier(0.34, 1.25, 0.64, 1);
    overflow: hidden;
  }

  @keyframes pj-fadein { from { opacity: 0 } to { opacity: 1 } }
  @keyframes pj-slidein {
    from { opacity: 0; transform: translate(-50%, calc(-50% + 24px)) scale(0.96); }
    to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  }

  /* Stripe */
  .pj-stripe {
    height: 4px;
    background: linear-gradient(90deg, #66C15E 0%, #34d399 50%, #10b981 100%);
  }

  /* Header */
  .pj-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 22px 28px 14px;
    background: linear-gradient(180deg, #fafdf9 0%, #ffffff 100%);
    border-bottom: 1px solid #edf7ec;
  }
  .pj-header-left { flex: 1; }

  .pj-badge {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: #edf7ec;
    color: #276722;
    font-size: 11px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 20px;
    margin-bottom: 8px;
    letter-spacing: 0.03em;
    border: 1px solid #d4eed2;
    text-transform: uppercase;
  }
  .pj-badge-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #66C15E;
    box-shadow: 0 0 0 3px rgba(102, 193, 94, 0.25);
    animation: pj-pulse 2s infinite;
  }
  @keyframes pj-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(0.8); }
  }

  .pj-title {
    font-size: 21px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 4px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .pj-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    line-height: 1;
  }

  .pj-subtitle {
    font-size: 13.5px;
    color: #64748b;
    margin: 0;
    line-height: 1.45;
  }

  .pj-close {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s ease;
    margin-top: 2px;
  }
  .pj-close:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #dc2626;
    transform: rotate(90deg);
  }

  /* Progress */
  .pj-progress-wrap {
    padding: 12px 28px 4px;
    background: #fafdf9;
  }
  .pj-progress-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
  }
  .pj-progress-label {
    display: inline-flex;
    align-items: center;
    font-size: 11.5px;
    font-weight: 600;
    color: #4b5563;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .pj-progress-pct {
    font-size: 11.5px;
    font-weight: 700;
    color: #16a34a;
  }
  .pj-progress-track {
    height: 6px;
    background: #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
  }
  .pj-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #66C15E 0%, #34d399 100%);
    border-radius: 6px;
    transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* Body */
  .pj-body {
    padding: 18px 28px 16px;
    overflow-y: auto;
    flex: 1 1 auto;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
  }
  .pj-body::-webkit-scrollbar {
    width: 6px;
  }
  .pj-body::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 4px;
  }

  .pj-callout {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
    border: 1px solid #bbf7d0;
    border-left: 4px solid #16a34a;
    border-radius: 12px;
    padding: 12px 16px;
    margin-bottom: 20px;
    font-size: 13.5px;
    color: #166534;
    line-height: 1.5;
  }
  .pj-callout-icon {
    color: #16a34a;
    flex-shrink: 0;
    margin-top: 2px;
  }

  /* Fields */
  .pj-fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
  }
  .pj-field { display: flex; flex-direction: column; }
  .pj-field.full { grid-column: 1 / -1; }

  .pj-label {
    font-size: 13px;
    font-weight: 600;
    color: #334155;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .pj-req { color: #ef4444; margin-left: 2px; }

  .pj-input, .pj-dialog .form-control {
    width: 100%;
    min-height: 44px;
    border-radius: 10px !important;
    border: 1px solid #cbd5e1 !important;
    background: #f8fafc !important;
    padding: 10px 14px !important;
    font-size: 14px !important;
    font-family: inherit;
    color: #0f172a !important;
    outline: none;
    transition: all 0.2s ease !important;
    box-sizing: border-box;
  }
  .pj-input:focus, .pj-dialog .form-control:focus {
    border-color: #66C15E !important;
    background: #ffffff !important;
    box-shadow: 0 0 0 3px rgba(102, 193, 94, 0.18) !important;
  }
  .pj-input::placeholder, .pj-dialog .form-control::placeholder {
    color: #94a3b8 !important;
  }

  textarea.pj-input, .pj-dialog textarea.form-control {
    padding: 12px 14px !important;
    min-height: 88px !important;
    resize: vertical;
  }

  /* Footer */
  .pj-footer {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 28px 22px;
    background: #fafdf9;
    border-top: 1px solid #edf7ec;
  }

  .pj-btn-skip {
    flex: 0 0 auto;
    height: 44px;
    padding: 0 20px;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    color: #64748b;
    font-size: 13.5px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
  }
  .pj-btn-skip:hover {
    background: #f1f5f9;
    color: #1e293b;
    border-color: #cbd5e1;
  }

  .pj-btn-save {
    flex: 1;
    height: 44px;
    border-radius: 10px;
    border: none;
    background: linear-gradient(135deg, #66C15E 0%, #46aa3e 100%);
    box-shadow: 0 4px 14px rgba(102, 193, 94, 0.35);
    color: #fff;
    font-size: 14.5px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s ease;
  }
  .pj-btn-save:hover:not(:disabled) {
    background: linear-gradient(135deg, #57b34f 0%, #3e9c36 100%);
    box-shadow: 0 6px 18px rgba(102, 193, 94, 0.45);
    transform: translateY(-1px);
  }
  .pj-btn-save:active:not(:disabled) {
    transform: translateY(0);
  }
  .pj-btn-save:disabled {
    cursor: default;
    opacity: 0.7;
  }
  .pj-btn-save.loading {
    background: #57b34f;
  }
  .pj-btn-save.success {
    background: #15803d;
  }

  .pj-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255,255,255,0.35);
    border-top-color: #fff;
    border-radius: 50%;
    animation: pj-spin 0.7s linear infinite;
  }
  @keyframes pj-spin { to { transform: rotate(360deg); } }

  /* Responsive */
  @media (max-width: 600px) {
    .pj-fields { grid-template-columns: 1fr; }
    .pj-field.full { grid-column: 1; }
    .pj-header { padding: 18px 20px 14px; }
    .pj-body { padding: 16px 20px; }
    .pj-footer { padding: 14px 20px 18px; }
    .pj-progress-wrap { padding: 10px 20px 0; }
  }
      `}</style>
    </>
  );
}
